/**
 * Format a decimal coordinate to a fixed number of decimal places.
 *
 * Shared across `EventLocusMap`, `EventDetailTemplate` (venue card), and any
 * other surface that renders a mono-family lat/lon label — keeps the rounding
 * consistent so coordinates don't visually drift when the precision changes.
 *
 * @example
 *   formatCoord(35.2)        // "35.2000"
 *   formatCoord(-80.843178)  // "-80.8432"
 *   formatCoord(35.22708, 6) // "35.227080"
 */
export function formatCoord(n: number, fractionDigits = 4): string {
  return n.toFixed(fractionDigits);
}

/**
 * Format a single decimal coordinate as a degree string with cardinal
 * hemisphere suffix, e.g. `35.2271° N` or `80.8431° W`.
 *
 * @param value - Decimal-degree value (positive or negative).
 * @param axis  - `'lat'` resolves N/S; `'lng'` resolves E/W.
 *
 * @example
 *   formatCoordDegree(35.2271, 'lat')   // "35.2271° N"
 *   formatCoordDegree(-80.8431, 'lng')  // "80.8431° W"
 */
export function formatCoordDegree(value: number, axis: 'lat' | 'lng'): string {
  const hemisphere = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
  return `${Math.abs(value).toFixed(4)}° ${hemisphere}`;
}

/**
 * Format a lat/lng pair as an interpunct-separated degree string,
 * e.g. `35.2271° N · 80.8431° W`.
 *
 * @example
 *   formatCoordPair(35.2271, -80.8431)  // "35.2271° N · 80.8431° W"
 */
export function formatCoordPair(lat: number, lng: number): string {
  return `${formatCoordDegree(lat, 'lat')} · ${formatCoordDegree(lng, 'lng')}`;
}
